import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Breadcrumbs,
  Link,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  useNavigate,
  useParams,
  Link as RouterLink,
  useLocation,
} from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import dayjs from 'dayjs';
import {
  getDocumentEditView,
  getCategories,
  getSubCategories,
  updateDocument,
  getBundleList,
  createDocumentBundle,
  addDocumentToBundle,
  createDocumentVersion,
  getDocumentPermissions,
  setDocumentPermissions,
  removeDocumentPermissions,
} from '../../endpoints/documents';
import { GroupPermission } from '../../types/group';
import {
  DocumentBundleListItem,
  DocumentUpdate,
  DocumentVersionCreate,
} from '../../types/document';
import { GenericResponse } from '../../types/generic';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const DocumentEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [showBundleDialog, setShowBundleDialog] = useState(false);

  const [showAddToBundleModal, setShowAddToBundleModal] = useState(false);
  const [bundleSearchTerm, setBundleSearchTerm] = useState('');
  const [availableBundles, setAvailableBundles] = useState<
    DocumentBundleListItem[]
  >([]);
  const [isLoadingBundles, setIsLoadingBundles] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedBundleOrder, setSelectedBundleOrder] = useState<{
    [bundleId: string]: number;
  }>({});
  const [bundleFormData, setBundleFormData] = useState({
    name: '',
    description: '',
  });

  // Version form data
  const [versionFormData, setVersionFormData] = useState<DocumentVersionCreate>(
    {
      version: '',
      description: '',
    }
  );

  // Form state for editing
  const [editFormData, setEditFormData] = useState<DocumentUpdate>({
    name: '',
    description: '',
    category_ids: [],
    sub_category_ids: [],
    tags: [],
    whats_new_start_date: null,
    whats_new_end_date: null,
  });

  // Separate state for raw tag input to allow typing commas and spaces
  const [rawTagInput, setRawTagInput] = useState<string>('');

  // Document permissions state
  const [documentRestricted, setDocumentRestricted] = useState<boolean>(false);
  const [documentPermissions, setDocumentPermissionsState] = useState<string[]>(
    []
  );
  const [permissionsError, setPermissionsError] = useState<string | null>(null);

  // Ref to track if we've processed permissions from navigation state
  const processedPermissionsRef = useRef<string | null>(null);

  // Fetch document data
  const {
    data: document,
    isLoading,
    error,
    refetch: refetchDocument,
  } = useApiGet(['document-edit', id], () => getDocumentEditView(id!), {
    enabled: !!id,
  });

  // Fetch categories and sub-categories
  const { data: categories = [] } = useApiGet(['categories'], getCategories);
  const { data: subCategories = [] } = useApiGet(
    ['sub-categories'],
    getSubCategories
  );

  // Filter sub-categories based on selected categories
  const filteredSubCategories = subCategories.filter((subCat) =>
    editFormData.category_ids.includes(subCat.category_id)
  );

  // Mutation for setting document permissions
  const { mutate: setDocumentPermissionsMutation } = useApiSend(
    (groupTypeIds: string[]) => {
      if (!id) throw new Error('Document ID is required');
      return setDocumentPermissions(id, groupTypeIds);
    },
    (data) => {
      console.log('Document permissions saved:', data);
      setPermissionsError(null);
      // Refetch permissions to update state
      if (editMode && documentRestricted) {
        refetchPermissions();
      }
      // Refetch document data to update the restricted tag
      refetchDocument();
    },
    (error) => {
      console.error('Error saving document permissions:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setPermissionsError(`Failed to save permissions: ${errorMessage}`);
    },
    undefined
  );

  // Mutation for removing document permissions
  const { mutate: removeDocumentPermissionsMutation } = useApiSend<
    GenericResponse,
    void
  >(
    () => {
      if (!id) throw new Error('Document ID is required');
      return removeDocumentPermissions(id);
    },
    (data) => {
      console.log('Document permissions removed:', data);
      setDocumentPermissionsState([]);
      setPermissionsError(null);
      // Refetch document data to update the restricted tag
      refetchDocument();
    },
    (error) => {
      console.error('Error removing document permissions:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setPermissionsError(`Failed to remove permissions: ${errorMessage}`);
    },
    undefined
  );

  // Handle return from GroupPermissions page
  useEffect(() => {
    const state = location.state as {
      groupPermissions?: GroupPermission[];
    } | null;
    if (state?.groupPermissions) {
      // Create a unique key for this permissions state to prevent duplicate processing
      const permissionsKey = JSON.stringify(state.groupPermissions);

      // Skip if we've already processed this exact permissions state
      if (processedPermissionsRef.current === permissionsKey) {
        return;
      }

      // Mark as processed immediately
      processedPermissionsRef.current = permissionsKey;

      // Clear the navigation state immediately to prevent re-processing
      window.history.replaceState({}, window.document.title);

      // Extract group_type_ids from GroupPermission array
      const groupTypeIds = state.groupPermissions.map((gp) => gp.group_type_id);

      // Validate: if documentRestricted is true, must have at least one group_type_id
      if (documentRestricted && groupTypeIds.length === 0) {
        setPermissionsError(
          'At least one group type must be selected when document is restricted.'
        );
        return;
      }

      // Save permissions via API
      if (id) {
        if (groupTypeIds.length > 0) {
          setDocumentPermissionsMutation(groupTypeIds);
        } else {
          // If no groups selected and restricted is false, remove permissions
          removeDocumentPermissionsMutation(undefined);
        }
      }
    }
  }, [
    location.state,
    documentRestricted,
    id,
    setDocumentPermissionsMutation,
    removeDocumentPermissionsMutation,
  ]);

  // Calculate if document is currently "new" based on dates
  const isCurrentlyNew = (): boolean => {
    if (!document?.whats_new_start_date || !document?.whats_new_end_date) {
      return false;
    }
    const today = dayjs().startOf('day');
    const startDate = dayjs(document.whats_new_start_date).startOf('day');
    const endDate = dayjs(document.whats_new_end_date).startOf('day');
    return (
      (today.isAfter(startDate) || today.isSame(startDate)) &&
      today.isBefore(endDate)
    );
  };

  // Populate form data when document loads
  useEffect(() => {
    if (document) {
      setEditFormData({
        name: document.name,
        description: document.description || '',
        category_ids: document.categories.map((cat) => cat.id),
        sub_category_ids: document.sub_categories.map((subCat) => subCat.id),
        tags: document.tags,
        whats_new_start_date: document.whats_new_start_date || null,
        whats_new_end_date: document.whats_new_end_date || null,
      });
      // Also populate raw tag input
      setRawTagInput(document.tags.join(', '));
      // Set document restricted state
      setDocumentRestricted(document.document_restricted || false);
      // Reset permissions state
      setDocumentPermissionsState([]);
    }
  }, [document]);

  // Fetch permissions when entering edit mode and document_restricted is true
  const {
    data: permissionsData,
    isLoading: loadingPermissions,
    refetch: refetchPermissions,
  } = useApiGet<{ group_type_ids: string[] }>(
    ['document-permissions', id],
    () => getDocumentPermissions(id!),
    {
      enabled: editMode && documentRestricted && !!id,
    }
  );

  // Update permissions state when fetched
  useEffect(() => {
    if (permissionsData?.group_type_ids) {
      setDocumentPermissionsState(permissionsData.group_type_ids);
    }
  }, [permissionsData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleInputChange = (field: keyof DocumentUpdate, value: any) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedCategories =
      typeof value === 'string' ? value.split(',') : value;
    handleInputChange('category_ids', selectedCategories);

    // Clear sub-categories that don't belong to the new selected categories
    if (selectedCategories.length === 0) {
      handleInputChange('sub_category_ids', []);
    } else {
      const currentSubCategories = editFormData.sub_category_ids;
      const validSubCategories = currentSubCategories.filter((subCatId) => {
        const subCat = subCategories.find((sc) => sc.id === subCatId);
        return subCat && selectedCategories.includes(subCat.category_id);
      });
      handleInputChange('sub_category_ids', validSubCategories);
    }
  };

  const handleSubCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedSubCategories =
      typeof value === 'string' ? value.split(',') : value;
    handleInputChange('sub_category_ids', selectedSubCategories);
  };

  const removeCategory = (categoryId: string) => {
    const newCategories = editFormData.category_ids.filter(
      (id) => id !== categoryId
    );
    handleInputChange('category_ids', newCategories);

    // Also remove sub-categories that don't belong to remaining categories
    const remainingSubCategories = editFormData.sub_category_ids.filter(
      (subCatId) => {
        const subCat = subCategories.find((sc) => sc.id === subCatId);
        return subCat && newCategories.includes(subCat.category_id);
      }
    );
    handleInputChange('sub_category_ids', remainingSubCategories);
  };

  const removeSubCategory = (subCategoryId: string) => {
    const newSubCategories = editFormData.sub_category_ids.filter(
      (id) => id !== subCategoryId
    );
    handleInputChange('sub_category_ids', newSubCategories);
  };

  const handleTagsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRawTagInput(value);

    // Parse tags from the raw input for storage
    const parsedTags = value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
    handleInputChange('tags', parsedTags);
  };

  // Update document mutation
  const { mutate: updateDocumentMutation, isPending: isUpdating } = useApiSend(
    (data: DocumentUpdate) => updateDocument(id!, data),
    (data) => {
      console.log('Document updated successfully:', data);
      setEditMode(false);
      // Clear permissions error on successful save
      setPermissionsError(null);
      // Show success message
      if (data.message) {
        alert(data.message);
      } else {
        alert('Document updated successfully!');
      }
      // Refresh the document data
      window.location.reload();
    },
    (error) => {
      console.error('Error updating document:', error);
      // Show error message to user
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Error updating document: ${errorMessage}`);
    },
    [['document-edit', id]]
  );

  // Create bundle mutation
  const { mutate: createBundleMutation, isPending: isCreatingBundle } =
    useApiSend(
      (data: { name: string; description: string }) =>
        createDocumentBundle({
          ...data,
          document_id: id!,
        }),
      (data) => {
        console.log('Bundle created successfully:', data);
        setShowBundleDialog(false);
        setBundleFormData({ name: '', description: '' });
        // Refresh the document data to show the new bundle
        window.location.reload();
      },
      (error) => {
        console.error('Error creating bundle:', error);
      },
      [['document-edit', id]]
    );

  // Create document version mutation
  const { mutate: createVersionMutation, isPending: isCreatingVersion } =
    useApiSend<GenericResponse>(
      (data: DocumentVersionCreate) => createDocumentVersion(id!, data),
      (data) => {
        console.log('Version created successfully:', data);
        setShowVersionDialog(false);
        setVersionFormData({ version: '', description: '' });

        // Go to version details page
        navigate(`/library/${id}/versions/${data.message}`);
      },
      (error) => {
        console.error('Error creating version:', error);
        alert('Error creating version. Please try again.');
      },
      [['document-edit', id]]
    );

  const handleSave = () => {
    // Validate tags format before saving
    const invalidTags = editFormData.tags.filter((tag) => {
      // Check for tags with spaces (should be single words)
      if (tag.includes(' ')) {
        return true;
      }
      // Check for tags that are too long
      if (tag.length > 50) {
        return true;
      }
      // Check for tags with special characters (allow alphanumeric and hyphens)
      if (!/^[a-zA-Z0-9-]+$/.test(tag)) {
        return true;
      }
      return false;
    });

    if (invalidTags.length > 0) {
      alert(
        `Invalid tags: ${invalidTags.join(', ')}. Tags should be single words with only letters, numbers, and hyphens.`
      );
      return;
    }

    // Validate: if documentRestricted is true, must have at least one group_type_id
    if (documentRestricted && documentPermissions.length === 0) {
      setPermissionsError(
        'At least one group type must be selected when document is restricted. Please manage group permissions before saving.'
      );
      alert(
        'Cannot save: Document is restricted but no group permissions are set. Please select at least one group type.'
      );
      return;
    }

    // Validate: if whats_new is checked, both dates must be provided
    const whatsNewChecked =
      editFormData.whats_new_start_date && editFormData.whats_new_end_date;
    if (
      whatsNewChecked &&
      (!editFormData.whats_new_start_date || !editFormData.whats_new_end_date)
    ) {
      alert(
        'Both start date and end date are required when marking document as "What\'s New".'
      );
      return;
    }

    // Include document_restricted in update
    const updateData: DocumentUpdate = {
      ...editFormData,
      document_restricted: documentRestricted ? 1 : 0,
      // Only include dates if both are provided (checkbox is effectively checked)
      whats_new_start_date: whatsNewChecked
        ? editFormData.whats_new_start_date
        : null,
      whats_new_end_date: whatsNewChecked
        ? editFormData.whats_new_end_date
        : null,
    };

    updateDocumentMutation(updateData);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleCreateVersion = () => {
    setVersionFormData({ version: '', description: '' });
    setShowVersionDialog(true);
  };

  const handleCreateBundle = () => {
    setBundleFormData({ name: '', description: '' });
    setShowBundleDialog(true);
  };

  const handleVersionInputChange = (
    field: keyof DocumentVersionCreate,
    value: string
  ) => {
    setVersionFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateVersionSubmit = () => {
    if (versionFormData.version.trim()) {
      createVersionMutation(versionFormData);
    }
  };

  const handleDeleteBundle = (bundleId: string) => {
    // TODO: Implement delete bundle functionality
    console.log('Deleting bundle:', bundleId);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading document...</Typography>
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading document</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/library"
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          Documents
        </Link>
        <Typography variant="body2" color="text.primary">
          {document.name}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {document.name}
        </Typography>
        <Stack direction="row" spacing={1}>
          {editMode ? (
            <>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isUpdating}
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#0072CE',
                '&:hover': { backgroundColor: '#005a9e' },
              }}
              startIcon={<EditIcon />}
              onClick={handleEditToggle}
            >
              Edit
            </Button>
          )}
        </Stack>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="document tabs"
        >
          <Tab label="Overview" />
          <Tab label="Versions" />
          <Tab label="Bundles" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6">Document Information</Typography>
              {!editMode && document.document_restricted && (
                <Chip
                  label="Restricted"
                  color="warning"
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    height: '24px',
                  }}
                />
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Document Name"
                  value={editFormData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={!editMode}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={editFormData.description}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  disabled={!editMode}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Categories</InputLabel>
                  <Select
                    multiple
                    value={editFormData.category_ids}
                    onChange={handleCategoryChange}
                    input={<OutlinedInput label="Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const category = categories.find(
                            (cat) => cat.id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={category?.name || value}
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation();
                                removeCategory(value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              deleteIcon={<CloseIcon />}
                              sx={{
                                '& .MuiChip-deleteIcon': {
                                  color: '#666',
                                  '&:hover': {
                                    color: '#333',
                                  },
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Sub-Categories</InputLabel>
                  <Select
                    multiple
                    value={editFormData.sub_category_ids}
                    onChange={handleSubCategoryChange}
                    input={<OutlinedInput label="Sub-Categories" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const subCategory = subCategories.find(
                            (subCat) => subCat.id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={subCategory?.name || value}
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation();
                                removeSubCategory(value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              deleteIcon={<CloseIcon />}
                              sx={{
                                '& .MuiChip-deleteIcon': {
                                  color: '#666',
                                  '&:hover': {
                                    color: '#333',
                                  },
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {filteredSubCategories.map((subCategory) => (
                      <MenuItem key={subCategory.id} value={subCategory.id}>
                        {subCategory.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tags"
                  value={rawTagInput}
                  onChange={handleTagsChange}
                  placeholder="Enter tags separated by commas (e.g., official, draft, internal)"
                  helperText="Tags help organize and categorize documents. Enter multiple tags separated by commas."
                  disabled={!editMode}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              </Grid>
              {/* What's New Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, fontWeight: 'bold' }}
                >
                  What's New
                </Typography>
                {!editMode ? (
                  // View mode: Show checkbox as checked if currently new, and display end date
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isCurrentlyNew()}
                          disabled
                          color="primary"
                        />
                      }
                      label="Mark as What's New"
                    />
                    {isCurrentlyNew() && document?.whats_new_end_date && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 4, mt: 1 }}
                      >
                        Until:{' '}
                        {dayjs(document.whats_new_end_date).format(
                          'MM/DD/YYYY'
                        )}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  // Edit mode: Show checkbox and date fields with shortcuts
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            !!(
                              editFormData.whats_new_start_date &&
                              editFormData.whats_new_end_date
                            )
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              // When checked, set default dates (30 days)
                              const today = dayjs().format('YYYY-MM-DD');
                              const endDate = dayjs()
                                .add(30, 'day')
                                .format('YYYY-MM-DD');
                              handleInputChange('whats_new_start_date', today);
                              handleInputChange('whats_new_end_date', endDate);
                            } else {
                              // When unchecked, keep dates but they won't be sent to API
                              // (handled in save handler)
                            }
                          }}
                          color="primary"
                        />
                      }
                      label="Mark as What's New"
                    />
                    {editFormData.whats_new_start_date &&
                      editFormData.whats_new_end_date && (
                        <Box sx={{ ml: 4, mt: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontWeight: 'medium' }}
                          >
                            Quick Set:
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            {[30, 60, 90, 120].map((days) => (
                              <Button
                                key={days}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const today = dayjs().format('YYYY-MM-DD');
                                  const endDate = dayjs()
                                    .add(days, 'day')
                                    .format('YYYY-MM-DD');
                                  handleInputChange(
                                    'whats_new_start_date',
                                    today
                                  );
                                  handleInputChange(
                                    'whats_new_end_date',
                                    endDate
                                  );
                                }}
                              >
                                {days} Days
                              </Button>
                            ))}
                          </Stack>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Start Date"
                                type="date"
                                value={editFormData.whats_new_start_date || ''}
                                onChange={(e) =>
                                  handleInputChange(
                                    'whats_new_start_date',
                                    e.target.value || null
                                  )
                                }
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="End Date"
                                type="date"
                                value={editFormData.whats_new_end_date || ''}
                                onChange={(e) =>
                                  handleInputChange(
                                    'whats_new_end_date',
                                    e.target.value || null
                                  )
                                }
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                required
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                  </Box>
                )}
              </Grid>
              {editMode && (
                <>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={documentRestricted}
                            onChange={(e) => {
                              const newRestricted = e.target.checked;
                              setDocumentRestricted(newRestricted);
                              setPermissionsError(null);

                              // If enabling restrictions, automatically navigate to permissions page
                              if (
                                newRestricted &&
                                documentPermissions.length === 0
                              ) {
                                navigate('/library/group-permissions', {
                                  state: {
                                    initialSelections: [],
                                    enableRoleSelection: false,
                                    returnPath: `/library/${id}/edit`,
                                  },
                                });
                              } else if (!newRestricted) {
                                // If disabling restrictions, remove permissions
                                if (documentPermissions.length > 0) {
                                  removeDocumentPermissionsMutation(undefined);
                                }
                              }
                            }}
                            color="primary"
                          />
                        }
                        label="Restrict Document Access"
                      />
                      {loadingPermissions && (
                        <Typography variant="body2" color="text.secondary">
                          Loading permissions...
                        </Typography>
                      )}
                    </Box>
                    {documentRestricted && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, ml: 4 }}
                      >
                        This document will only be accessible to selected group
                        types.
                        {documentPermissions.length > 0 && (
                          <>
                            {' '}
                            {documentPermissions.length} group type(s) currently
                            selected.
                          </>
                        )}
                      </Typography>
                    )}
                    {permissionsError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {permissionsError}
                      </Alert>
                    )}
                  </Grid>
                  {editMode && documentRestricted && (
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          // Convert group_type_ids to GroupPermission format
                          const initialSelections: GroupPermission[] =
                            documentPermissions.map((groupTypeId) => ({
                              group_type_id: groupTypeId,
                              all_roles: true,
                              role_ids: undefined,
                            }));

                          navigate('/library/group-permissions', {
                            state: {
                              initialSelections,
                              enableRoleSelection: false,
                              returnPath: `/library/${id}/edit`,
                            },
                          });
                        }}
                        disabled={loadingPermissions}
                        sx={{
                          color: '#0072CE',
                          borderColor: '#0072CE',
                          '&:hover': {
                            borderColor: '#005a9e',
                            backgroundColor: 'rgba(0, 114, 206, 0.04)',
                          },
                        }}
                      >
                        Manage Group Permissions
                      </Button>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Document Versions</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateVersion}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            Add Version
          </Button>
        </Box>
        <Card>
          <CardContent>
            <List>
              {document.versions.map((versionObj, index) => (
                <ListItem
                  key={versionObj.id}
                  divider={index < document.versions.length - 1}
                  onClick={() =>
                    navigate(`/library/${id}/versions/${versionObj.id}`)
                  }
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 107, 53, 0.08)',
                      borderRadius: 1,
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <ListItemText
                    primary={`Version ${versionObj.version}`}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {versionObj.is_active && (
                          <Chip
                            label="Active"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {versionObj.is_public && (
                          <Chip
                            label="Public"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {versionObj.is_draft && (
                          <Chip
                            label="Draft"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {!versionObj.is_active &&
                          !versionObj.is_public &&
                          !versionObj.is_draft && (
                            <Chip
                              label="Inactive"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Version Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click when clicking icon
                            navigate(
                              `/library/${id}/versions/${versionObj.id}`
                            );
                          }}
                          sx={{
                            color: '#FF6B35',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 107, 53, 0.12)',
                            },
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Document Bundles</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowAddToBundleModal(true)}
              sx={{
                color: '#00A859',
                borderColor: '#00A859',
                '&:hover': {
                  borderColor: '#008a4a',
                  backgroundColor: 'rgba(0, 168, 89, 0.04)',
                },
              }}
            >
              Add to Existing Bundle
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateBundle}
              sx={{
                backgroundColor: '#00A859',
                '&:hover': { backgroundColor: '#008a4a' },
              }}
            >
              Create Bundle
            </Button>
          </Box>
        </Box>
        <Grid container spacing={2}>
          {document.bundles.map((bundle) => (
            <Grid item xs={12} md={6} key={bundle.id}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="h6" component="h3">
                      {bundle.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          sx={{ color: '#0072CE' }}
                          onClick={() =>
                            navigate(`/library/bundles/${bundle.id}`)
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          sx={{ color: '#D32F2F' }}
                          onClick={() => handleDeleteBundle(bundle.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Version Dialog */}
      <Dialog
        open={showVersionDialog}
        onClose={() => setShowVersionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Version</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Version Number"
            value={versionFormData.version}
            onChange={(e) =>
              handleVersionInputChange('version', e.target.value)
            }
            placeholder="e.g., 1.1, 2024.2"
            sx={{ mb: 2, mt: 1 }}
            disabled={isCreatingVersion}
          />
          <TextField
            fullWidth
            size="small"
            label="Description"
            value={versionFormData.description}
            onChange={(e) =>
              handleVersionInputChange('description', e.target.value)
            }
            multiline
            rows={3}
            placeholder="What changed in this version?"
            disabled={isCreatingVersion}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowVersionDialog(false)}
            disabled={isCreatingVersion}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateVersionSubmit}
            disabled={!versionFormData.version.trim() || isCreatingVersion}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            {isCreatingVersion ? 'Creating...' : 'Create Version'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bundle Dialog */}
      <Dialog
        open={showBundleDialog}
        onClose={() => setShowBundleDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Bundle</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Bundle Name"
                value={bundleFormData.name}
                onChange={(e) =>
                  setBundleFormData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="Enter bundle name..."
                disabled={isCreatingBundle}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={bundleFormData.description}
                onChange={(e) =>
                  setBundleFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                multiline
                rows={3}
                placeholder="Describe what this bundle contains..."
                disabled={isCreatingBundle}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowBundleDialog(false)}
            disabled={isCreatingBundle}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (bundleFormData.name.trim()) {
                createBundleMutation(bundleFormData);
              }
            }}
            disabled={isCreatingBundle || !bundleFormData.name.trim()}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            {isCreatingBundle ? 'Creating...' : 'Create Bundle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Existing Bundle Modal */}
      <Dialog
        open={showAddToBundleModal}
        onClose={() => setShowAddToBundleModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Document to Existing Bundle</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search Bundles"
              placeholder="Search for bundles by name or description..."
              value={bundleSearchTerm}
              onChange={(e) => setBundleSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={async () => {
                setIsLoadingBundles(true);
                setHasSearched(true);
                try {
                  const bundles = await getBundleList(
                    bundleSearchTerm || undefined
                  );
                  setAvailableBundles(bundles);
                } catch (error) {
                  console.error('Error fetching bundles:', error);
                  setAvailableBundles([]);
                } finally {
                  setIsLoadingBundles(false);
                }
              }}
              disabled={isLoadingBundles}
            >
              {isLoadingBundles ? 'Searching...' : 'Search'}
            </Button>
          </Box>

          {isLoadingBundles ? (
            <Typography>Searching for bundles...</Typography>
          ) : hasSearched && availableBundles.length > 0 ? (
            <List>
              {availableBundles.map((bundle) => (
                <ListItem
                  key={bundle.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={bundle.name}
                    secondary={bundle.description || 'No description'}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label="Order"
                      type="number"
                      size="small"
                      value={selectedBundleOrder[bundle.id] || ''}
                      onChange={(e) => {
                        const order = parseInt(e.target.value) || 0;
                        setSelectedBundleOrder((prev) => ({
                          ...prev,
                          [bundle.id]: order,
                        }));
                      }}
                      placeholder="0"
                      sx={{ width: 100 }}
                      inputProps={{ min: 0 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        const order = selectedBundleOrder[bundle.id] || 0;
                        if (order < 0) {
                          alert('Order must be a non-negative number');
                          return;
                        }

                        try {
                          await addDocumentToBundle(bundle.id, {
                            document_id: id!,
                            order: order,
                          });
                          console.log(
                            `Document ${id} added to bundle ${bundle.id} at order ${order}`
                          );
                          setShowAddToBundleModal(false);
                          // Refresh the document data to show the new bundle
                          window.location.reload();
                        } catch (error) {
                          console.error(
                            'Error adding document to bundle:',
                            error
                          );
                          alert(
                            'Failed to add document to bundle. Please try again.'
                          );
                        }
                      }}
                      disabled={
                        !selectedBundleOrder[bundle.id] &&
                        selectedBundleOrder[bundle.id] !== 0
                      }
                      sx={{
                        color: '#00A859',
                        borderColor: '#00A859',
                        '&:hover': {
                          borderColor: '#008a4a',
                          backgroundColor: 'rgba(0, 168, 89, 0.04)',
                        },
                      }}
                    >
                      Add to Bundle
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : hasSearched && bundleSearchTerm && !isLoadingBundles ? (
            <Typography color="text.secondary">
              No bundles found matching "{bundleSearchTerm}"
            </Typography>
          ) : hasSearched ? (
            <Typography color="text.secondary">
              No bundles found. Try adjusting your search terms.
            </Typography>
          ) : (
            <Typography color="text.secondary">
              Search for bundles to add this document to
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddToBundleModal(false);
              setHasSearched(false);
              setAvailableBundles([]);
              setBundleSearchTerm('');
              setSelectedBundleOrder({});
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentEdit;
